import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';

export type WalleyState =
  | 'idle'
  | 'wave'
  | 'think'
  | 'walk'
  | 'nod'
  | 'shake'
  | 'jump'
  | 'look-left'
  | 'look-right'
  | 'sleep'
  | 'celebrate'
  | 'shrug';

/** Drop-in, dependency-free (apart from Angular) animated Walley character. */
@Component({
  selector: 'app-walley',
  standalone: true,
  templateUrl: './walley.component.html',
  styleUrls: ['./walley.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WalleyComponent implements OnInit, OnDestroy {
  /** Width in CSS pixels. Height follows the SVG viewBox. */
  @Input() size = 240;

  /** Set true when nearby text already describes the robot. */
  @Input() decorative = false;

  /** Occasionally plays a gesture in place. */
  @Input() ambient = false;

  /** `head` shows only the face, for the header icon. */
  @Input() crop: 'full' | 'head' = 'full';

  /** Unique suffix so several robots can share one page. */
  readonly artId = `walley-${Math.random().toString(36).slice(2, 9)}`;

  @Output() stateChange = new EventEmitter<WalleyState>();
  @Output() movementEnd = new EventEmitter<void>();

  state: WalleyState = 'idle';
  x = 0;
  y = 0;
  moveDuration = 0;

  private actionTimer?: ReturnType<typeof setTimeout>;
  private movementTimer?: ReturnType<typeof setTimeout>;
  private ambientTimer?: ReturnType<typeof setTimeout>;
  private actionToken = 0;
  private movementToken = 0;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  get accessibleLabel(): string {
    const labels: Record<WalleyState, string> = {
      idle: 'Walley, robot in attesa',
      wave: 'Walley saluta',
      think: 'Walley pensa',
      walk: 'Walley cammina',
      nod: 'Walley annuisce',
      shake: 'Walley scuote la testa',
      jump: 'Walley salta',
      'look-left': 'Walley guarda a sinistra',
      'look-right': 'Walley guarda a destra',
      sleep: 'Walley dorme',
      celebrate: 'Walley festeggia',
      shrug: 'Walley fa spallucce',
    };
    return labels[this.state];
  }

  /** Play a state, then return to idle. Pass 0 to keep it active. */
  play(state: WalleyState, durationMs = 2200): void {
    this.cancelAction();
    this.cancelMovement();
    this.moveDuration = 0;
    this.setState(state);
    this.cdr.markForCheck();

    if (state !== 'idle' && Number.isFinite(durationMs) && durationMs > 0) {
      const token = this.actionToken;
      this.actionTimer = setTimeout(() => {
        if (token === this.actionToken) this.setState('idle');
      }, durationMs);
    }
  }

  /** Move relative to the original position, with walking legs during travel. */
  moveTo(x: number, y: number, durationMs = 1200): void {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    this.cancelMovement();
    this.cancelAction();

    const requestedDuration = Number.isFinite(durationMs)
      ? Math.max(0, durationMs)
      : 1200;
    const reduceMotion = typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const duration = reduceMotion ? 0 : requestedDuration;
    this.moveDuration = duration;
    this.x = x;
    this.y = y;
    this.setState(duration > 0 ? 'walk' : 'idle');
    this.cdr.markForCheck();

    if (duration === 0) {
      this.movementEnd.emit();
      return;
    }

    const token = this.movementToken;
    this.movementTimer = setTimeout(() => {
      if (token !== this.movementToken) return;
      this.setState('idle');
      this.movementEnd.emit();
    }, duration);
  }

  /** Stop the current action and any active movement at the current target. */
  stop(): void {
    this.cancelAction();
    this.cancelMovement();
    this.moveDuration = 0;
    this.setState('idle');
    this.cdr.markForCheck();
  }

  /** Animate back to the starting point. */
  resetPosition(durationMs = 800): void {
    this.moveTo(0, 0, durationMs);
  }

  onPointerEnter(): void {
    if (this.prefersReducedMotion() || this.movementTimer) {
      return;
    }
    let gesture = this.pickGesture();
    if (gesture === this.state) {
      gesture = this.pickGesture();
    }
    this.play(gesture, this.gestureDuration(gesture));
  }

  ngOnInit(): void {
    if (!this.ambient || this.prefersReducedMotion()) {
      return;
    }
    this.queueAmbient(this.crop === 'head' ? 2500 : 700, this.crop === 'head' ? 'look-right' : 'wave');
  }

  ngOnDestroy(): void {
    this.cancelAction();
    this.cancelMovement();
    if (this.ambientTimer) {
      clearTimeout(this.ambientTimer);
    }
  }

  private setState(state: WalleyState): void {
    if (this.state === state) return;
    this.state = state;
    this.stateChange.emit(state);
    this.cdr.markForCheck();
  }

  private cancelAction(): void {
    this.actionToken++;
    if (this.actionTimer) clearTimeout(this.actionTimer);
    this.actionTimer = undefined;
  }

  private queueAmbient(delayMs: number, gesture?: WalleyState): void {
    this.ambientTimer = setTimeout(() => {
      const next = gesture ?? this.pickGesture();
      this.play(next, this.gestureDuration(next));
      const pause = 9000 + Math.round(Math.random() * 8000);
      this.queueAmbient(pause);
    }, delayMs);
  }

  private pickGesture(): WalleyState {
    if (this.crop === 'head') {
      const faces: WalleyState[] = ['look-left', 'look-right', 'nod', 'shake', 'nod'];
      return faces[Math.floor(Math.random() * faces.length)];
    }
    const gestures: WalleyState[] = [
      'wave',
      'wave',
      'think',
      'walk',
      'nod',
      'shake',
      'jump',
      'look-left',
      'look-right',
      'sleep',
      'celebrate',
      'shrug',
    ];
    return gestures[Math.floor(Math.random() * gestures.length)];
  }

  private gestureDuration(state: WalleyState): number {
    switch (state) {
      case 'think':
      case 'sleep':
        return 2600;
      case 'look-left':
      case 'look-right':
      case 'walk':
      case 'shrug':
        return 1600;
      case 'nod':
      case 'shake':
        return 1400;
      case 'jump':
        return 900;
      default:
        return 1800;
    }
  }

  private prefersReducedMotion(): boolean {
    return typeof window !== 'undefined'
      && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  private cancelMovement(): void {
    this.movementToken++;
    if (this.movementTimer) clearTimeout(this.movementTimer);
    this.movementTimer = undefined;
  }
}
